import {
  AfterViewInit,
  ApplicationRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl, FormGroup } from '@angular/forms';
import {
  PDFReportInformation,
  AcceptableFileTypes,
  PrintColorsConstant,
  DescriptorFile,
  FileSelected,
  IS_SERVER_PLATFORM,
} from 'src/app/core';
import {
  AnimationBuilderService,
  BindingsService,
  FilesService,
  MainLogoAnimService,
  OrderService,
  PrintBlockService,
  StockService,
  TitleService,
  TypingsService,
} from 'src/app/feature/services';
import {
  IntroService,
  OrderTypes,
  PrintBlockItem,
  ThesisOrderEditDto,
} from 'src/app/shared';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { PrintOrderComponent } from '../print-order/print-order.component';
import { ComponentBusService } from '../../services/component-bus.service';
import { DataSharingService } from 'src/app/shared/services/data-sharing.service';

@Component({
  selector: 'app-thesis-edit-order',
  templateUrl: './thesis-edit-order.component.html',
  styleUrls: ['./thesis-edit-order.component.scss',
  '../print-order/print-order.component.scss',],
})
export class ThesisEditOrderComponent
  extends PrintOrderComponent
  implements OnInit, AfterViewInit
{
  @Input('state') activatedState!: 'thesis' | 'edit';
  @Output('changeState') changeState: EventEmitter<'thesis' | 'edit'> =
    new EventEmitter();

  private docReport!: FileSelected;
  private descriptorFile!: DescriptorFile;

  private availableDescriptorFileFormat: AcceptableFileTypes[] = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/msword',
    'application/pdf',
  ];

  public readyToSubmit: boolean = false;

  formGroup = new FormGroup({ moreDetails: new FormControl('') });

  // Constructors -------------------------------------------------------->

  constructor(
    animationBuilder: AnimationBuilderService,
    mainLogoAnimation: MainLogoAnimService,
    breakpointObserver: BreakpointObserver,
    printBlockService: PrintBlockService,
    bindingsService: BindingsService,
    typingsService: TypingsService,
    activatedRoute: ActivatedRoute,
    introJSService: IntroService,
    titleService: TitleService,
    filesService: FilesService,
    orderService: OrderService,
    stockService: StockService,
    snackbar: MatSnackBar,
    busService: ComponentBusService,
    dataSharingService: DataSharingService,

    @Inject(IS_SERVER_PLATFORM) isServer: boolean,

    router: Router,
    _ngZone: NgZone,

    appRef: ApplicationRef,
    private snack: MatSnackBar
  ) {
    super(
      router,
      _ngZone,
      filesService,
      titleService,
      orderService,
      typingsService,
      bindingsService,
      printBlockService,
      breakpointObserver,
      snackbar,
      mainLogoAnimation,
      activatedRoute,
      animationBuilder,
      isServer,
      stockService,
      introJSService,
      busService,
      dataSharingService
    );
  }

  public getValidTypes() {
    return this.availableDescriptorFileFormat;
  }

  public getValidTypesHelp() {
    const formatsString = ['pdf', 'doc', 'dot', 'docx', 'dotx'].join(' ,');
    return 'فرمت مجاز: ' + formatsString;
  }

  override ngAfterViewInit(): void {
    if (!this.isServer) {
      this.introJs.addSteps([
        {
          element: '#thesisFilePlaceholder .file-upload-placeholder-wrapper',
          title: 'انتخاب فایل مقاله، پایان‌نامه',
          intro: 'فایل اصلی مقاله یا پایان‌نامه خود را آپلود کنید.',
        },
  
        {
          element: '#descriptorFilePlaceholder .file-upload-placeholder-wrapper',
          intro:
            'فایل فرمتی که دانشگاه‌آن را به شما داده است را با فرمت‌های PDF و یا Word آپلود کنید تا ما بتوانیم طبق فایل فرمت شما، پایان‌نامه شما را ویرایش کنیم.',
          title: 'آپلود فایل فرمت مقاله یا پایان‌نامه',
        },
  
        {
          element: '#printOrderExtraInformation .mdc-notched-outline',
          title: 'توضیحات',
          intro:
            'توضیحات خود را برای کارشناسان ما در این کادر بنویسید. نوشتن توضیحات الزامی نیست.',
        },
  
        {
          element: '#submitBtn',
          title: 'ثبت سفارش',
          intro:
            'با کلیک روی این دکمه درخواست شما ثبت خواهد شد و پس از تعیین قیمت توسط اپراتور‌های ما، شما توسط پیامک یا ایمیل مطلع خواهید شد. سپس می‌توانید سفارش خود را نهایی کنید.',
        },
      ]);
  
      this.introJs.setOptions({
        nextLabel: 'بعدی',
        prevLabel: 'قبلی',
        doneLabel: 'اتمام',
        scrollToElement: false,
      });
    }
  }

  changeStateTo(state: 'thesis' | 'edit') {
    this.changeState.emit(state);
  }

  onDocFileReport(report: PDFReportInformation | FileSelected) {
    this.docReport = report as FileSelected;
  }

  onDescriptorUpload(uploadResult: any): void {
    this.descriptorFile = uploadResult.file.file;
  }

  onDescriptorFileUpload() {
    this.onDescriptorFileUpload();
  }

  /* The submit method were
   we post our method to the server or local db */
  override submit(): any {
    if (this.validToSubmit()) {
      const orderObject = this.getOrderObject();

      this.orderService
        .save({ order: orderObject, type: 'thesis-edit' })
        .then((data) => {});
    } else {
      this.sayError('سفارش شما ثبت نشد. لطفا مقادیر را برسی کنید');
    }
  }

  public override getPrintType(): OrderTypes {
    return 'thesis-edit';
  }

  public validToSubmit(): boolean {
    if (!this.docReport) return false;
    if (!this.descriptorFile) return false;

    return true;
  }

  private getOrderObject(): ThesisOrderEditDto {
    const docFileReport = this.docReport.file as any as PDFReportInformation;
    if (!docFileReport.uploaded?.id) throw new Error('File not uploaded');

    const moreDetails = this.formGroup.get('moreDetails')?.value;
    const numpages = (this.docReport.file as any as PDFReportInformation)
      .numpages;

    return {
      blocks: [this.generateBlock()],
      fileIds: [docFileReport.uploaded.id, this.descriptorFile.id],
      moreDetails: moreDetails!,
      pageCount: numpages,
      type: 'thesis-edit',
      copiesNumber: 1,
      isMixed: false,
      // price: '0',
      color: PrintColorsConstant.BLACK_WHITE,
    };
  }

  generateBlock(): PrintBlockItem {
    const docFileReport = this.docReport.file as any as PDFReportInformation;

    return {
      id: 0,
      start: 0,
      // isLocal: true,
      // actualData: '',
      end: docFileReport.numpages,
      typeId: this.pageColor.value,
    };
  }

  public override startHelp() {
    if (!this.isServer) this.introJs.start();
  }

  // ----------------------------- Private methods|

  private sayError(msg: string) {
    this.snack.open(msg, 'باشه', { duration: 1500 });
  }
}
